import { Injectable } from "@angular/core";
import { SortByAttribute, compile } from "@hsgovtech/hsutils";
import { groupBy } from "lodash-es";
import { InspectionRepository } from "../repositories/inspection.repository";
import { LocationRepository } from "../repositories/location.repository";
import { ArrayClientRepository } from "../repositories/abstract/array.client.repository";
import { ClientService } from "./client.service";
import { IrefTabsRepository } from "../repositories/iref.tabs.repository";
import { TabDataRepository } from "../repositories/tab.data.repository";
import { InspectionViolationRepository } from "../repositories/inspection.violation.repository";
import { BaseService } from "./base.service";
import { LogService } from "./log.service";
import { IrefItemObservationRepository } from "../repositories/iref.item.obs.repository";
import { IrefItemCodeRepository } from "../repositories/iref.item.code.repository";
import { IrefItemRepository } from "../repositories/iref.item.repository";
import { isUUID } from "../../utils";
import { IrefCategoriesRepository } from "../repositories/iref.category.repository";
import { Schema } from "../types/DataService/Schema";

@Injectable({ providedIn: "root" })
export class DataService extends BaseService {
  constructor(
    private clientService: ClientService,
    private inspectionRepo: InspectionRepository,
    private locationRepo: LocationRepository,
    private tabRepo: IrefTabsRepository,
    private tabDataRepo: TabDataRepository,
    private violationItemRepo: IrefItemRepository,
    private observationRepo: IrefItemObservationRepository,
    private violationCodeRepo: IrefItemCodeRepository,
    private inspectionViolationRepo: InspectionViolationRepository,
    private categoryRepo: IrefCategoriesRepository,
    logService: LogService,
  ) {
    super(logService, "DataService");
  }

  private cachedSchemas: any = {};

  private sortViolations = (a: any, b: any): number => {
    // Step 1: Sort by category sort order
    if (a.item.categoryID && b.item.categoryID) {
      const categorySortA = parseInt(
        this.categoryRepo.getById(a.item.categoryID).sort,
        10,
      );
      const categorySortB = parseInt(
        this.categoryRepo.getById(b.item.categoryID).sort,
        10,
      );

      if (
        Number.isInteger(categorySortA) &&
        Number.isInteger(categorySortB) &&
        categorySortA !== categorySortB
      ) {
        return categorySortA - categorySortB;
      }
    }

    // Step 2: If in the same category, sort by item.sort if both are integers
    const sortValueA = parseInt(a.item.sort, 10);
    const sortValueB = parseInt(b.item.sort, 10);

    if (
      Number.isInteger(sortValueA) &&
      Number.isInteger(sortValueB) &&
      sortValueA !== sortValueB
    ) {
      return sortValueA - sortValueB;
    }

    // Step 3: Sort by item number if they have the same sort value
    const numA = parseInt(a.item.num, 10);
    const numB = parseInt(b.item.num, 10);

    if (Number.isInteger(numA) && Number.isInteger(numB) && numA !== numB) {
      return numA - numB;
    }

    // Step 4: If numbers are also equal, compare letters
    const letterA = a.item.let;
    const letterB = b.item.let;

    if (letterA < letterB) {
      return -1;
    }
    if (letterA > letterB) {
      return 1;
    }

    // If all criteria are equal, items are considered the same in sort order
    return 0;
  };

  private getViolationItemsByInspectionID = (inspectionID: any) => {
    const inspection: any =
      this.inspectionRepo.getInspectionByInspectionID(inspectionID);
    const categories = this.categoryRepo.getIrefCategoriesByInspectionTypeID(
      inspection.inspectionTypeID,
    );
    const sortedCategories = SortByAttribute(categories, "sort");
    const items = sortedCategories.flatMap((category: any) => {
      const catItems = this.violationItemRepo.getIrefItemsByCategoryID(
        category.categoryID,
      );
      return SortByAttribute(catItems, "sort");
    });
    return items;
  };

  private aggregateViolations = (useItemIndex = false) => {
    const violations: any = this.getViolations()[1];
    const violationRecords = violations.getAsArray();
    const sorted = violationRecords.sort(this.sortViolations);
    const grouped = groupBy(sorted, "itemID");
    const res = Object.values(grouped).map((records: any) => {
      return this.violationItemAggregator(records, useItemIndex);
    });
    return res;
  };

  private getVioData = () => {
    const res = this.aggregateViolations(true);
    const sorted = SortByAttribute(res, "itemIndex");
    const getAsArray = () =>
      sorted.reduce((acc: any, r: any) => {
        const gapSize = Math.max(0, r.itemIndex - acc.length);
        const filler = Array(gapSize).fill(undefined);
        return [...acc, ...filler, r.status.observation];
      }, []);
    return ["violationID", { getAsArray }];
  };

  private getVioFields = () => {
    const res = this.aggregateViolations(true);
    const sorted = SortByAttribute(res, "itemIndex");
    const getAsArray = () =>
      sorted.reduce((acc: any, r: any) => {
        const gapSize = Math.max(0, r.itemIndex - acc.length);
        const filler = Array(gapSize).fill(undefined);
        return [...acc, ...filler, r.details];
      }, []);
    return ["violationID", { getAsArray }];
  };

  private getVioFieldsNonIndex = () => {
    const res = this.aggregateViolations();
    const getAsArray = () =>
      res.flatMap((r: any) => r.instances).sort(this.sortViolations);
    return ["violationID", { getAsArray }];
  };

  private getPhotos = () => {
    const selected =
      this.clientService.getCurrentClient().clientData.selectedInspection;
    const getAsArray = () => selected.photos;
    return ["violationID", { getAsArray }];
  };

  private violationItemAggregator(records: any, useItemIndex = false): any {
    const firstViolationRecord = records[0];
    const category = this.categoryRepo.getById(
      firstViolationRecord.item.categoryID,
    );
    const violationItem = this.violationItemRepo.getById(
      firstViolationRecord.itemID,
    );

    const topObservation = records.reduce(
      (acc: any, record: any) => {
        const observation = this.observationRepo.getById(record.observationID);
        const sortNumber = parseInt(observation.sort || "0", 10);
        if (sortNumber < acc.sortNumber) {
          return { num: sortNumber, observation };
        }
        return acc;
      },
      { sortNumber: Number.MAX_SAFE_INTEGER, observation: {} },
    ).observation;

    const codes = records.flatMap((record: any) => {
      let codeIds = record.codeID || record.code || record.codeId;
      if (!Array.isArray(codeIds)) {
        codeIds = [codeIds];
      }
      const res = codeIds.map((id: string) => {
        return this.violationCodeRepo.getById(id) || {};
      });
      return res;
    });

    const comments = records.reduce((acc: any, record: any) => {
      if (record.comments && record.comments.trim() !== "") {
        return `${
          acc + compile("{num} {let}. ", record) + record.comments.trim()
        }\n`;
      }
      return acc;
    }, "");

    const instances = records;

    const items = this.getViolationItemsByInspectionID(
      firstViolationRecord.inspectionID,
    );
    const itemIndex = items.findIndex(
      (i: any) => i.itemID === firstViolationRecord.itemID,
    );

    return {
      itemIndex: useItemIndex
        ? itemIndex
        : parseInt(firstViolationRecord.sort, 10) - 1,
      comments,
      status: topObservation,
      details: {
        ...firstViolationRecord,
        observation: topObservation.observation,
        codeInfo: codes[0],
        codesInfo: codes,
        category,
        item: violationItem,
      },
      instances,
      count: records.length,
      codeInfo: codes[0],
      codesInfo: codes,
    };
  }

  public getRepo(name: string, prefix = "sch") {
    const prefixedName = `${prefix}${name}`;
    if (prefixedName === "schLocation") {
      return this.getLocations();
    }

    if (prefixedName === "schqLocation") {
      return this.getSelectedLocation();
    }

    if (prefixedName === "schqListScreen") {
      return this.getListScreen();
    }

    if (prefixedName === "schqInsp") {
      return this.getSelectedInspection();
    }

    if (prefixedName === "schInspection") {
      return this.getInspections();
    }

    if (prefixedName === "schqVio") {
      return this.getVioData();
    }

    if (prefixedName === "schqPhotos") {
      return this.getPhotos();
    }

    if (prefixedName === "schqVioFieldsIndexed") {
      return this.getVioFields();
    }

    if (
      prefixedName === "schqVioFields" ||
      prefixedName === "schqVioFieldsNonIndex"
    ) {
      return this.getVioFieldsNonIndex();
    }

    if (
      prefixedName === "schViolation" ||
      prefixedName === "schInspection_Violation" ||
      prefixedName === "schInspectionViolation"
    ) {
      return this.getViolations();
    }

    if (this.isTab(name)) {
      return this.getTabs(name);
    }
    const pk = this.getPrimaryKey(name, prefix);
    const repo = new ArrayClientRepository(
      this.clientService,
      `schemas.${prefixedName}.records`,
      pk,
    );
    return [pk, repo];
  }

  public isTab(name: string) {
    if (isUUID(name)) {
      return true;
    }
    if (
      this.tabRepo.getFilteredByValue("srcData", this.handleTabName(name))
        .length
    ) {
      return true;
    }
    return false;
  }

  private getTabSchema = (name: string) => {
    if (isUUID(name)) {
      const tabSchema = this.tabRepo.getByValue("tabID", name);
      return tabSchema;
    }
    return this.tabRepo.getByValue("srcData", this.handleTabName(name));
  };

  private handleTabName(name: string) {
    if (name.substring(0, 3) === "Tab") {
      return name.replace("Tab", "Reload");
    }
    if (name.substring(0, 6) === "schTab") {
      return name.replace("schTab", "Reload");
    }
    return name;
  }

  public getTabs(name: string) {
    const inspection =
      this.clientService.getCurrentClient().clientData.selectedInspection;

    if (inspection) {
      const schema = this.getTabSchema(name);
      const prefixedName = schema.srcData;
      const getAsArray = () =>
        this.tabDataRepo.getChildRecords(inspection, prefixedName);
      const getById = (id: string) =>
        this.tabDataRepo.getChildRecordsFilteredByValues(
          inspection,
          prefixedName,
          [schema.pkey],
          [id],
        )[0];
      const removeMultiple = (primaryKey: any, values: any) =>
        this.tabDataRepo.removeMultipleChildRecords(
          inspection,
          prefixedName,
          primaryKey,
          values,
        );
      const getFilteredByValues = (keys: any, values: any) =>
        this.tabDataRepo.getChildRecordsFilteredByValues(
          inspection,
          prefixedName,
          keys,
          values,
        );
      const saveRecord = (primaryKey: any, record: any) =>
        this.tabDataRepo.saveChildRecords(
          inspection,
          prefixedName,
          primaryKey,
          record,
        );
      return [
        schema.pkey,
        {
          observable: this.tabDataRepo.getChildRecordsObservable(
            inspection,
            prefixedName,
          ),
          getAsArray,
          getFilteredByValues,
          removeMultiple,
          getById,
          saveRecord,
        },
      ];
    }
    return [];
  }

  public getInspections() {
    const getAsArray = () =>
      this.inspectionRepo
        .getAllInspectionsAsArray()
        .map((inspection: any) => inspection.record);
    const getById = (id: string) =>
      this.inspectionRepo.getInspectionByInspectionID(id);
    const removeMultiple = async (primaryKey: any, values: any) => {
      const res = await this.inspectionRepo.removeMultipleInspections(values);
      return Object.values(res);
    };
    const getFilteredByValues = (keys: any, values: any) =>
      Object.values(this.inspectionRepo.searchInspections(keys, values));
    const saveRecord = async (primaryKey: any, record: any) => {
      const res = await this.inspectionRepo.saveInspection(record);
      return Object.values(res);
    };
    return [
      "inspectionID",
      { getAsArray, getFilteredByValues, removeMultiple, getById, saveRecord },
    ];
  }

  public getUsers() {
    if (this.cachedSchemas.schUsers) {
      return this.cachedSchemas.schUsers;
    }
    const repo = new ArrayClientRepository(
      this.clientService,
      `schemas.schUsers.records`,
      "userID",
    );
    const userMap = repo.getAsMap();
    this.cachedSchemas.schUsers = userMap;
    return userMap;
  }

  private getSelectedInspection() {
    const selected =
      this.clientService.getCurrentClient().clientData.selectedInspection;
    const getAsArray = () => selected.record;
    const getById = (id: string) =>
      this.inspectionRepo.getInspectionByInspectionID(id)?.record;
    return ["inspectionID", { getAsArray, getById }];
  }

  private getSelectedLocation() {
    const selected =
      this.clientService.getCurrentClient().clientData.selectedInspection;
    const location = this.locationRepo.getById(selected.locationID);
    const getAsArray = () => location.record;
    const getById = (id: string) => this.locationRepo.getById(id)?.record;
    return ["inspectionID", { getAsArray, getById }];
  }

  private getListScreen() {
    const selected =
      this.clientService.getCurrentClient().clientData.selectedInspection;
    const location = this.locationRepo.getById(selected.locationID);
    const getAsArray = () => ({
      ...selected.record,
      ...location.record,
      ...location.output,
    });
    return ["inspectionID", { getAsArray }];
  }

  private getLocations() {
    const getAsArray = () =>
      this.locationRepo.getAsArray().map((l) => {
        return l.record;
      });
    const getById = (id: string) => this.locationRepo.getById(id);
    const removeMultiple = async (primaryKey: any, values: any) => {
      const res = await this.locationRepo.removeMultiple(values);
      return Object.values(res);
    };
    const getFilteredByValues = (keys: any, values: any) =>
      Object.values(this.locationRepo.getFilteredByValues(keys, values));
    const saveRecord = async (primaryKey: any, record: any) => {
      const res = await this.locationRepo.saveRecord(primaryKey, record);
      return Object.values(res);
    };
    return [
      "locationID",
      { getAsArray, getFilteredByValues, removeMultiple, getById, saveRecord },
    ];
  }

  private getViolationsWithObservations(violations: any[]) {
    return violations.map((violation: any) => {
      const observation = this.observationRepo.getById(violation.observationID);
      const item = this.violationItemRepo.getById(violation.itemID);

      if (violation.codeID && !Array.isArray(violation.codeID)) {
        const code = this.violationCodeRepo.getById(violation.codeID);
        return {
          ...observation,
          ...violation,
          codeInfo: code,
          codesInfo: [],
          item,
        };
      }
      if (violation.codeID && Array.isArray(violation.codeID)) {
        const codes = this.violationCodeRepo.getFilteredByMultiValue(
          "codeID",
          violation.codeID,
        );
        return {
          ...observation,
          ...violation,
          codeInfo: codes[0],
          codesInfo: codes,
          item,
        };
      }
      return { ...observation, ...violation, item };
    });
  }

  public getViolations(): any {
    const inspection =
      this.clientService.getCurrentClient().clientData.selectedInspection;
    const getAsArray = () => {
      const violations =
        this.inspectionViolationRepo.getInspectionViolationsByInspectionID(
          inspection.locationID,
          inspection.inspectionID,
        );
      return this.getViolationsWithObservations(violations);
    };
    const getById = (id: string) => {
      return this.getViolationsWithObservations([
        this.inspectionViolationRepo.getInspectionViolationByID(
          inspection.locationID,
          inspection.inspectionID,
          id,
        ),
      ])[0];
    };
    const removeMultiple = (primaryKey: any, values: any) =>
      this.inspectionViolationRepo.removeInspectionViolations(
        inspection.locationID,
        inspection.inspectionID,
        values,
      );
    const getFilteredByValues = (keys: any, values: any) => {
      const violations =
        this.inspectionViolationRepo.searchInspectionViolations(
          inspection.locationID,
          inspection.inspectionID,
          keys,
          values,
        );
      return this.getViolationsWithObservations(violations);
    };
    const saveRecord = (primaryKey: any, record: any) =>
      this.inspectionViolationRepo.saveInspectionViolation(
        inspection.locationID,
        inspection.inspectionID,
        record,
      );
    return [
      "violationID",
      { getAsArray, getFilteredByValues, removeMultiple, getById, saveRecord },
    ];
  }

  public getPrimaryKey = (name: string, prefix: string = "sch") => {
    return this.getKey(name, "pk", prefix);
  };

  public getForeignKey = (name: string, prefix: string = "sch") => {
    return this.getKey(name, "fk", prefix);
  };

  public getKey = (name: string, key: string, prefix: string = "sch") => {
    const schema =
      this.clientService.getCurrentClient().clientData?.schemas[
        `${prefix}${name}`
      ];
    if (!schema) {
      return "";
    }
    return schema[key];
  };

  public clearCache() {
    this.cachedSchemas = {};
  }

  public getAdditionalSchemas() {
    return Object.fromEntries(
      Object.entries(
        this.clientService.getCurrentClient().clientData?.schemas,
      ).filter(
        ([key]) =>
          !/^CFG|HSScripts|Users|User_Location|Inspection_Violation_Codes/i.test(
            key.replace("sch", ""),
          ),
      ),
    );
  }

  public doesTabSchemaExist(name: string): boolean {
    if (isUUID(name)) {
      return this.tabRepo.getByValue("tabID", name) !== undefined;
    }
    return (
      this.tabRepo.getFilteredByValue("srcData", this.handleTabName(name))
        .length > 0
    );
  }

  public getSchema(name: string, useCache = false): Schema {
    if (this.cachedSchemas[name] && useCache) {
      return this.cachedSchemas[name];
    }
    const [pk, repo] = this.getRepo(name);
    const fk = this.getForeignKey(name);
    const records = repo.getAsArray();
    const search = (values: any, keys: any): any =>
      repo.getFilteredByValues(keys, values);
    const remove = (value: any): any => repo.removeMultiple(pk, value);
    const getRecord = (id: string): any => repo.getById(id);
    const getRecordByFK = (value: string, key?: string): any =>
      repo.getFilteredByValues([key || fk], [value]);
    const save = (record: any): any => repo.saveRecord(pk, record);
    const schema = {
      observable: repo.observable,
      records,
      recordCount: records.length,
      search,
      remove,
      save,
      getRecord,
      getRecordByFK,
    };
    this.cachedSchemas[name] = schema;
    return schema;
  }
}
